import { useIsMutating, useMutationState } from '@tanstack/react-query';
import { useEffect } from 'react';

import { useAutosaveCue } from '@/core/hooks/use-autosave-cue';

/** Tag on every mutation that persists Setup data (the AI preview is not one). */
export const SETUP_SAVE_MUTATION_KEY = ['reputation-setup', 'save'] as const;

/**
 * Page-level autosave status for Setup, for the standard `AutosaveStatusCue`.
 * Derived from the tagged save mutations, so it stays idle on load and only
 * flashes saving → saved when something is actually persisted.
 */
export function useSetupAutosaveStatus() {
  const { status, syncFromPending } = useAutosaveCue();
  const pending = useIsMutating({ mutationKey: SETUP_SAVE_MUTATION_KEY }) > 0;
  const latest = useMutationState({
    filters: { mutationKey: SETUP_SAVE_MUTATION_KEY },
    select: (mutation) => ({
      status: mutation.state.status,
      at: mutation.state.submittedAt,
    }),
  })
    .filter((m) => m.at > 0)
    .sort((a, b) => b.at - a.at)[0];
  const errored = latest?.status === 'error';

  useEffect(() => {
    syncFromPending(pending, { errored });
  }, [pending, errored, syncFromPending]);

  return status;
}
